import { clientLogger } from '@/lib/clientLogger';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtime } from '@/providers/RealtimeProvider';

/**
 * Hook that listens for job invalidation events from the Realtime service.
 * When a JobUpdated event is received for the specified jobId,
 * it automatically invalidates the React Query cache for that job.
 *
 * Usage:
 *   useJobInvalidation(jobId);
 *
 * Place this in any component that displays a single job's data.
 */
export const useJobInvalidation = (jobId: string | undefined | null) => {
    const { connection } = useRealtime();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!connection || !jobId) return;

        const handler = (event: { job_id: string; reason: string }) => {
            if (event.job_id === jobId) {
                clientLogger.info(`[Realtime] Job ${jobId} invalidated (reason: ${event.reason})`);
                queryClient.invalidateQueries({ queryKey: ['job', jobId] });
            }
        };

        connection.on('JobUpdated', handler);

        return () => {
            connection.off('JobUpdated', handler);
        };
    }, [connection, jobId, queryClient]);
};
